import { useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Loader } from "@mantine/core";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { mvpApiGetNextPendingJobTaskOptions } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { StatsigProvider } from "statsig-react";
import { useProfileHome } from "./hooks/useProfileHome";
import { useCoverageBetaEnabled } from "./hooks/useCoverageBetaEnabled";
import { JobsTable } from "./pages/JobsTablePage";
import { Layout } from "./components/Layout";
import "@/utils/auth";
import { auth, signIn } from "@/utils/auth";
import { SsoGate } from "@labeling-tools/ui-components";
import { OosConfirmation } from "./pages/OosConfirmation";
import { OosSelection } from "./pages/OosSelection";
import { AdminOosConfirmation } from "./pages/AdminOosConfirmation";
import { AdminOosSelection } from "./pages/AdminOosSelection";
import { AdminCoverageTask } from "./pages/AdminCoverageTask";
import { AdminCoverageQaTask } from "./pages/AdminCoverageQaTask";
import { OosConfirmationQa } from "./pages/OosConfirmationQa";
import { OosConfirmationQaView } from "./pages/OosConfirmationQaView";
import { BoxDrawTask } from "./pages/BoxDrawTask";
import { CoverageTask } from "./pages/CoverageTask";
import { CoverageQaTask } from "./pages/CoverageQaTask";
import { CoverageQaFeedback } from "./pages/CoverageQaFeedback";
import { ProfilePage } from "./pages/ProfilePage";
import { ViewMode } from "./types";
import { DevFixtureProvider } from "./dev/DevFixtureProvider";
import { CoverageBetaTask } from "./pages/CoverageBetaTask";
import { AdminCoverageBetaTask } from "./pages/AdminCoverageBetaTask";
import { AdminMetricsPage } from "./pages/AdminMetricsPage";
import { AdminCoverageBetaQaTask } from "./pages/AdminCoverageBetaQaTask";
import { CoverageBetaQaFeedback } from "./pages/CoverageBetaQaFeedback";

/**
 * When Coverage Beta is enabled (the `fe_enable_coverage_beta` gate, or always
 * on the training build — see useCoverageBetaEnabled), coverage tasks redirect
 * to their coverage-beta equivalents (preserving query params). It is only
 * checked here at the route level so every entry point — job row clicks, play
 * mode, deep links — lands on the beta UI.
 */
const CoverageRoute = () => {
  const { enabled: enableCoverageBeta, isLoading } = useCoverageBetaEnabled();
  const { jobId } = useParams();
  const { search } = useLocation();

  if (isLoading) return null;
  return enableCoverageBeta ? (
    <Navigate to={`/jobs/${jobId}/coverage-beta${search}`} replace />
  ) : (
    <CoverageTask />
  );
};

/**
 * Coverage QA tasks come in two shapes: the legacy per-box QA (classic page)
 * and a whole-review QA lebron marks `data.qa_scope === "task"`, reviewed in
 * the editable Coverage Beta UI. A job can hold both while stores move over,
 * so this follows the next pending task rather than the job — it shares the
 * query (and cache) CoverageBetaTask reads, and re-picks as tasks advance.
 */
const CoverageQaRoute = () => {
  const { jobId = "" } = useParams();
  const { data: nextTask, isLoading } = useQuery({
    ...mvpApiGetNextPendingJobTaskOptions({ path: { job_id: jobId } }),
    enabled: !!jobId,
  });

  if (isLoading) {
    return (
      <div className="flex h-40 w-full items-center justify-center">
        <Loader />
      </div>
    );
  }
  const isTaskScoped =
    (nextTask?.data as { qa_scope?: string } | undefined)?.qa_scope === "task";
  return isTaskScoped ? <CoverageBetaTask qaEdit /> : <CoverageQaTask />;
};

const AdminCoverageRoute = () => {
  const { enabled: enableCoverageBeta, isLoading } = useCoverageBetaEnabled();
  const { jobId } = useParams();
  const { search } = useLocation();

  if (isLoading) return null;
  return enableCoverageBeta ? (
    <Navigate to={`/jobs/${jobId}/admin-coverage-beta${search}`} replace />
  ) : (
    <AdminCoverageTask />
  );
};

function AppRoutes() {
  const { profileAsHome, isLoading } = useProfileHome();

  // Don't render any gate-dependent UI (home route, header retailer filter,
  // sidebar tabs) until Statsig resolves — avoids a jobs-table→profile flicker.
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <Layout>
            <Routes>
              <Route
                path="/"
                element={profileAsHome ? <ProfilePage /> : <JobsTable />}
              />
              <Route path="/labeling" element={<JobsTable />} />
              <Route path="/jobs/:jobId/box-draw" element={<BoxDrawTask />} />
              <Route path="/jobs/:jobId/coverage" element={<CoverageRoute />} />
              <Route
                path="/jobs/:jobId/coverage-beta"
                element={<CoverageBetaTask />}
              />
              <Route
                path="/jobs/:jobId/confirmation"
                element={<OosConfirmation mode={ViewMode.VIEWER} />}
              />
              <Route
                path="/jobs/:jobId/admin-confirmation"
                element={<AdminOosConfirmation />}
              />
              <Route path="/jobs/:jobId/selection" element={<OosSelection />} />
              <Route
                path="/jobs/:jobId/confirmation-qa"
                element={<OosConfirmationQa />}
              />
              <Route
                path="/jobs/:jobId/confirmation-qa-feedback"
                element={<OosConfirmationQaView />}
              />
              <Route
                path="/jobs/:jobId/admin-selection"
                element={<AdminOosSelection />}
              />
              <Route
                path="/jobs/:jobId/admin-coverage"
                element={<AdminCoverageRoute />}
              />
              <Route
                path="/jobs/:jobId/admin-coverage-qa"
                element={<AdminCoverageQaTask />}
              />
              <Route
                path="/jobs/:jobId/coverage-qa"
                element={<CoverageQaRoute />}
              />
              <Route
                path="/jobs/:jobId/admin-coverage-beta"
                element={<AdminCoverageBetaTask />}
              />
              <Route
                path="/jobs/:jobId/coverage-beta-qa"
                element={<CoverageBetaTask qaMode />}
              />
              <Route
                path="/jobs/:jobId/admin-coverage-beta-qa"
                element={<AdminCoverageBetaQaTask />}
              />
              <Route
                path="/jobs/:jobId/coverage-beta-qa-feedback"
                element={<CoverageBetaQaFeedback />}
              />
              <Route
                path="/jobs/:jobId/coverage-qa-feedback"
                element={<CoverageQaFeedback />}
              />
              <Route path="/admin/metrics" element={<AdminMetricsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        }
      />
    </Routes>
  );
}

function App() {
  // Create a QueryClient instance
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1,
          },
        },
      }),
  );

  return (
    <SsoGate
      auth={auth}
      signIn={signIn}
      ssoLoginUrl={import.meta.env.VITE_SSO_LOGIN_URL}
      devMode={import.meta.env.DEV}
      appName="Out of Stock"
      ssoEnabled={false}
    >
      {(user) => (
        <QueryClientProvider client={queryClient}>
          <StatsigProvider
            sdkKey={import.meta.env.VITE_STATSIG_CLIENT_KEY}
            user={{
              userID: user.uid,
              email: user.email ?? undefined,
            }}
          >
            {import.meta.env.DEV ? (
              <DevFixtureProvider>
                <AppRoutes />
              </DevFixtureProvider>
            ) : (
              <AppRoutes />
            )}
          </StatsigProvider>
        </QueryClientProvider>
      )}
    </SsoGate>
  );
}

export default App;
